import Box from "@mui/material/Box";
import {ColDef, ICellRendererParams, IServerSideGetRowsParams,} from "ag-grid-community";
import {AgGridReact} from "ag-grid-react";
import * as React from "react";
import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import SectionHeader from "../../../../util/component/SectionHeader";
import {locationGroupAtom} from "../../state/locationGroupsRecoil";
import {selectedLocationGroupLocationAtom,} from "../../state/locationGroupLocationsRecoil";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {fromGenericBeToGenericFe, onGridReady, useActionColDef, resolveFilterCodes} from "../../utils";
import {DEFAULT_COL_DEF, SET_SNACKBAR} from "../../constants";
import {useLocationGroupRepository} from "../../api/repositories/LocationGroupRepository";
import {
    DialogInterface,
    FormDialog,
    GenericComponentModel,
    SelectElementModelBE,
    SelectElementModelFE,
} from "../../api/types/generic";
import {locationAtom} from "../../state/locationsRecoil";
import {
    FormLocationGroupLocationModel,
    FormSearchLocationGroupLocationModel,
    LocationGroupLocations,
    LocationGroupLocationsSaveRequest,
} from "../../api/types/locationGroupLocation";
import {Location, LocationModelFE} from "../../api/types/location";
import * as yup from "yup";
import {useLocationGroupLocationRepository} from "../../api/repositories/LocationGroupLocationRepository";
import {useAgGridLocale} from "../../../../config/globalConfig";
import {Button, Dialog, DialogActions, DialogContent, Grid, IconButton, Tooltip, useTheme} from "@mui/material";
import Loading from "../../common/components/Loading";
import SearchFilters from "../../common/components/SearchFilters";
import {FormAutocomplete} from "../../common/form-fields";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import AddIcon from "@mui/icons-material/Add";
import {FormProvider, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import BootstrapDialogTitle from "../../common/components/BootstrapDialogTitle";
import BasicToolbar from "../../common/components/BasicToolbar";
import DialogComponent from "../../common/components/DialogComponent";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Delete} from "@mui/icons-material";
import {useLocationRepository} from "../../api/repositories/LocationRepository";
import {searchedOWLocationGroupLocationAtom} from "../../state/owLocationGroupLocationsRecoil";
import {LocationGroupEntityResponse} from "../../api/types/locationGroup";

const LocationGroupLocation = () => {
    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData, globalData: {user}} = useContext(GlobalContext);

    // Use Ref
    const locationGroupGridRef = useRef<AgGridReact>(null);
    const locationGridRef = useRef<AgGridReact>(null);

    // Use AgGrid Locale
    const agGridLocale = useAgGridLocale();

    // Use Recoil
    const [locationGroups, setLocationGroups] = useRecoilState(locationGroupAtom);
    const [locations, setLocations] = useRecoilState(locationAtom);
    const [selectedLocationGroupLocation, setSelectedLocationGroupLocation] = useRecoilState(selectedLocationGroupLocationAtom);
    const [searchedLocationGroupLocation, setSearchedLocationGroupLocation] = useRecoilState(searchedOWLocationGroupLocationAtom);
    // Use Repository Hooks
    const {getLocationGroups} = useLocationGroupRepository();
    const {getLocationAll} = useLocationRepository();
    const {
        postSaveLocationGroupLocationAll,
        getLocationGroupLocationsPaged,
        getLocationsByLocationGroup,
        deleteLocationGroupLocation
    } = useLocationGroupLocationRepository();

    // Use State
    const [formDialog, setFormDialog] = useState<FormDialog | null>(null);
    const [dialog, setDialog] = useState<DialogInterface | null>(null);
    const [regions, setRegions] = useState<SelectElementModelFE[]>([]);
    const [nativeLocationGroups, setNativeLocationGroups] = useState<LocationGroupEntityResponse[]>([]);

    const handleGetStaticContent = async () => {
        try {
            const locationGroups: LocationGroupEntityResponse[] = await getLocationGroups(user.legalEntities.map((el:any) => el.name));
            setNativeLocationGroups(locationGroups)
            setLocationGroups(
                fromGenericBeToGenericFe(locationGroups).sort((a, b) =>
                    a.label.localeCompare(b.label)
                )
            );
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };

    //setto tutte le legalEntities options e le passo al filtro di ricerca ["all", "drivalia_it","drivalia_fr"]
    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    //se utente clicca su all, mando  ["drivalia_it","drivalia_fr"]
    const optionsFiltered = legalEntityOptions
        .filter(function (el) {
            return el.code !== "all";
        });

    const everyLegalEntitiesWithoutAll = legalEntityOptions
        .map((el) => el.code as string)
        .filter((el) => el !== "all");

    const [testFilters, setTestFilters] = useState<FormSearchLocationGroupLocationModel>({
        locationGroupName: "", locationName: "", legalEntities: everyLegalEntitiesWithoutAll
    })

    // Use Effect
    useEffect(() => {
        handleGetStaticContent();
        // eslint-disable-next-line
    }, []);

    const handleGetLocationGroupLocationsPage = () => {
        locationGroupGridRef.current!.api.refreshServerSide({purge: true});
    };

    const handleTriggerFilters = (entries: FormSearchLocationGroupLocationModel) => {
        const hardcodedFilter = Object.entries(entries).reduce((acc, [key, value]) => ({
            ...acc,
            [key]: {type: "contains", filter: value}
        }), {});

        locationGroupGridRef.current?.api.paginationGoToFirstPage();
        locationGroupGridRef.current?.api.setFilterModel(hardcodedFilter);
        locationGroupGridRef.current?.api.onFilterChanged();
        if (selectedLocationGroupLocation) setSelectedLocationGroupLocation(null);
        locationGroupGridRef?.current?.api?.deselectAll();
    };

    useEffect(() => {
        if (locationGroupGridRef?.current) {
            handleTriggerFilters({
                locationGroupName: testFilters.locationGroupName,
                locationName: testFilters.locationName,
                legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
            });
        }
    }, []);

    useEffect(() => {
        handleTriggerResetFilters()
    }, [])

    const handleTriggerResetFilters = () => {
        // LPP-24637: legalEntity e' obbligatoria per il servizio di pricing.
        // Il reset deve ripristinare i valori del profilo utente (come lo stato iniziale),
        // non svuotarli: con la lista vuota la ricerca viene rifiutata con 400 "Validation failure".
        setTestFilters({
            locationGroupName: "",
            locationName: "",
            legalEntities: everyLegalEntitiesWithoutAll,
        });
        handleTriggerFilters({
            locationGroupName: "",
            locationName: "",
            legalEntities: everyLegalEntitiesWithoutAll,
        });
    };

    const handleTriggerFiltersInitial = () => {
        handleTriggerFilters({
            locationGroupName: testFilters.locationGroupName,
            locationName: testFilters.locationName,
            legalEntities: resolveFilterCodes(testFilters.legalEntities, everyLegalEntitiesWithoutAll),
        });
    };

    // useEffect(() => {
    //     setTimeout(() => {
    //         handleTriggerFiltersInitial();
    //     }, 500);
    // }, [i18n.resolvedLanguage]);

    const handleConfirmSaveLocationGroupLocation = async (formValues: FormLocationGroupLocationModel) => {
        try {
            const saveRequestLocationGroup: LocationGroupLocationsSaveRequest = {
                locationGroupId: formValues.locationGroup?.code as number,
                locations: formValues.locations.map((location: LocationModelFE) => {
                    return {
                        id: location?.code as string,
                        name: location?.label as string,
                        region: location?.region as string,
                        dmsCluster: location?.dmsCluster as string,
                        legalEntity: nativeLocationGroups.find(el => el.name === formValues.locationGroup?.label)?.legalEntity as string
                    }
                })
            }
            await postSaveLocationGroupLocationAll(saveRequestLocationGroup);
            handleGetLocationGroupLocationsPage();
            setLocations([]);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
            setFormDialog(null);
            form.reset();

            if (
                (selectedLocationGroupLocation?.locationGroup?.id as number) !==
                (formValues?.locationGroup?.code as number)
            ) {
                locationGroupGridRef?.current?.api?.deselectAll();
                setSelectedLocationGroupLocation(null);
            }
        } catch (e) {
            dispatchGlobalData(SET_SNACKBAR.ERROR);
        }
    };

    // ************ FORM - START ************ //
    const validationSchema = yup
        .object()
        .shape({
            locationGroup: yup
                .object()
                .shape({
                    code: yup.number(),
                    label: yup.string(),
                })
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
            locations: yup
                .array()
                .of(
                    yup.object().shape({
                        code: yup.string(),
                        label: yup.string(),
                    })
                )
                .min(1)
                .nullable()
                .required(t("translation:validation.fieldMandatory")),
        })
        .defined();

    // Use Form
    const form = useForm<FormLocationGroupLocationModel>({
        defaultValues: {locationGroup: null, locations: []},
        resolver: yupResolver(
            validationSchema,
            {},
            {
                rawValues: true
            }
        ),
        shouldUnregister: false
    });
    const watchAllForm = form.watch();
    // ************ FORM - END ************ //

    // ************ AG GRID START ************ //
    // Use Query
    const locationsQuery = useQuery({
        queryKey: ["pricing_location_query"],
        queryFn: getLocationAll,
    });

    // Use Mutation
    const mutationDeleteLocationGroupLocation = useMutation({
        mutationFn: (id: string) =>
            deleteLocationGroupLocation({
                id: {
                    locationGroupId: selectedLocationGroupLocation?.locationGroup.id || 0,
                    locationId: id,
                    legalEntities:
                        selectedLocationGroupLocation?.legalEntities as SelectElementModelFE,
                },
            }),
        onSuccess: () => {
            handleGetLocationGroupLocationsPage();
        },
    });

    const handleConfirmDelete = async (deletingRowIndex: string) => {
        try {
            await mutationDeleteLocationGroupLocation.mutate(deletingRowIndex);
            setDialog(null);
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const deleteButtonClicked = (params: any) => {
        setDialog({
            open: true,
            message: t(
                "pricing:locations.dialogContents.removeLocationGroupLocation"
            ),
            onClickConfirm: () => handleConfirmDelete(params.data.id),
        });
    };

    const locationGroupColumnDefs: ColDef[] = [
        {
            headerName: t("pricing:locations.table.headers.locationGroup"),
            headerTooltip: t("pricing:locations.table.headers.locationGroup"),
            field: "locationGroupName",
            editable: false,
            cellRenderer: (params: ICellRendererParams) =>
                params.data.locationGroup.name,
            flex: 1,
            filter: "text",
        },
        {
            field: "locationName",
            hide: true,
            filter: "text",
        },
        {
            headerName: t("pricing:locations.table.headers.legalEntities"),
            headerTooltip: t("pricing:locations.table.headers.legalEntities"),
            field: "legalEntities",
            editable: false,
            flex: 1,
            filter: "text",
            cellRenderer: (params: ICellRendererParams) => t('translation:legalEntities.'.concat(params.data.locationGroup.legalEntity)),
            hide: user.legalEntities.length != 1 ? false : true
        },
    ];

    const {actionColDef} = useActionColDef();
    const locationColumnDefs: ColDef[] = [
        {
            headerName: t("pricing:locations.table.headers.location"),
            headerTooltip: t("pricing:locations.table.headers.location"),
            field: "name",
            flex: 1,
        },
        {
            headerName: t("pricing:locations.table.headers.region"),
            headerTooltip: t("pricing:locations.table.headers.region"),
            field: "region",
            flex: 1,
        },
        {
            ...actionColDef,
            cellRenderer: (params: ICellRendererParams) =>
                !!user.profiles.find((profile: any) => {
                    return profile.auths.find((auth: any) => {
                        return auth.name === "SCOPE_PRC_DELETE_LOCATION_GROUP_LOCATION";
                    });
                }) && (
                    <Tooltip
                        arrow
                        title={t("pricing:tooltips.delete")}
                        placement={"right-start"}
                    >
                        <IconButton
                            aria-label="close"
                            onClick={() => deleteButtonClicked(params)}
                            sx={{
                                margin: "auto",
                                color: theme.palette.accent.main,
                            }}
                        >
                            <Delete/>
                        </IconButton>
                    </Tooltip>
                ),
        },
    ];

    const defaultColDef = DEFAULT_COL_DEF;

    const createDatasource = () => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let sort =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? ""
                        : params.request.sortModel[0].sort;

                const {filterModel} = params.request;

                const filter: FormSearchLocationGroupLocationModel = Object.entries(
                    filterModel as Record<string, { filter?: string; type: string }>
                ).reduce(
                    (acc, [key, value]) => ({
                        ...acc,
                        [key]: value?.filter || "",
                    }),
                    testFilters
                );
                sort = sort === "locationGroupName" ? "locationGroup_name" : sort;

                const locationGroupLocationsPaged =
                    await getLocationGroupLocationsPaged({
                        ...filter,
                        sort: sort && order ? `${sort},${order}` : "",
                        size:
                            locationGroupGridRef?.current?.api?.paginationGetPageSize() || 10,
                        page:
                            locationGroupGridRef?.current?.api?.paginationGetCurrentPage() ||
                            0,
                    });
                if (locationGroupLocationsPaged.totalElements === 0) {
                    locationGroupGridRef.current!.api.showNoRowsOverlay();
                } else {
                    locationGroupGridRef.current!.api.hideOverlay();
                }

                params.success({
                    rowData: locationGroupLocationsPaged.content,
                    rowCount: locationGroupLocationsPaged.totalElements,
                });

                const selectedData: LocationGroupLocations[] =
                    locationGroupGridRef?.current?.api?.getSelectedRows() || [];
                if (selectedData?.length === 0) {
                    locationGridRef?.current?.api?.setRowData([]);
                    locationGridRef?.current?.api?.refreshCells();
                } else {
                    const locationGroupLocations: LocationGroupLocations =
                        selectedData[0] as LocationGroupLocations;
                    const locations: Location[] = locationGroupLocations?.locations || [];
                    locationGridRef?.current?.api?.setRowData(locations);
                    locationGridRef?.current?.api?.refreshCells();
                }
            },
        };
    };
    // ************ AG GRID END ************ //

    const onGroupSelectionChange = useCallback(() => {
        const selectedRows: any[] =
            locationGroupGridRef.current!.api.getSelectedRows();
        const locationGroupLocations: LocationGroupLocations =
            selectedRows[0] as LocationGroupLocations;
        const locations: Location[] = locationGroupLocations?.locations || [];
        setSelectedLocationGroupLocation(locationGroupLocations);
        locationGridRef?.current?.api?.setRowData(locations);
        locationGridRef?.current?.api?.refreshCells();
    }, []);

    const filters: GenericComponentModel[] = [
        {
            name: "locationGroupName",
            value: testFilters.locationGroupName as string,
            label: `${t("pricing:locations.form.labels.locationGroup")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    locationGroupName: value,
                }),
            options: [],
            type: "TextField",
        },
        {
            name: "locationName",
            value: testFilters.locationName as string,
            label: `${t("pricing:locations.form.labels.location")}`,
            handleChange: (value: string) =>
                setTestFilters({
                    ...testFilters,
                    locationName: value,
                }),
            options: [],
            type: "TextField",
        },
        {
            name: "legalEntity",
            value: testFilters.legalEntities as SelectElementModelFE,
            label: `${t("pricing:locations.form.labels.legalEntity")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, legalEntities: value}),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length != 1 ? false : true
        }
    ];

    const handleChangeLocationGroup = async (value: SelectElementModelFE) => {
        form.setValue("locationGroup", value);
        form.setValue("locations", []);
        const locations = await getLocationsByLocationGroup(value.code as number, [value.legalEntity as string]);
        const locationsFe: LocationModelFE[] = fromGenericBeToGenericFe<
            Location,
            LocationModelFE
        >(locations).sort((a: LocationModelFE, b: LocationModelFE) =>
            a.label.localeCompare(b.label)
        );
        setLocations([
            {
                code: 0,
                label: t("pricing:actions.selectAll"),
                region: null,
            },
            ...locationsFe,
        ]);

        setRegions(
            locationsFe
                .reduce((acc: LocationModelFE[], el: LocationModelFE) => {
                    const alreadyPresent = acc.find((l) => l.region === el.region);
                    if (!alreadyPresent && el.region) acc.push(el);
                    return acc;
                }, [])
                .map((l) => ({
                    code: l.region as string,
                    label: l.region as string,
                }))
                .sort((a, b) => a.label.localeCompare(b.label))
        );
    };

    const handleChangeRegion = async (value: SelectElementModelFE) => {
        form.setValue("region", value);

        if (value)
            form.setValue(
                "locations",
                locations.filter((location) => location.region === value.code)
            );
        else form.setValue("locations", []);
    };

    const handleChangeLocations = (input: LocationModelFE[]) => {
        const value: LocationModelFE[] =
            // è stato cliccato "selectAll"
            input.find((el: LocationModelFE) => el.code === 0) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm.locations?.find((el: LocationModelFE) => el.code === 0)
                ? // applica il "selectAll
                locations.filter((el: LocationModelFE) => el.code !== 0)
                : // setta solo locations selezionati
                input.filter((el) => el.code !== 0);
        form.setValue("locations", value);
    };

    const formLocationGroupLocation = (
        <FormProvider {...form}>
            <Grid container spacing={2} alignItems="start">
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...form.register("locationGroup")}
                        label={`${t("pricing:locations.form.labels.locationGroup")}*`}
                        options={locationGroups}
                        multiple={false}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        handleChange={handleChangeLocationGroup}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...form.register("region")}
                        label={t("pricing:locations.table.headers.region")}
                        options={regions}
                        multiple={false}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        handleChange={handleChangeRegion}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...form.register("locations")}
                        label={`${t("pricing:locations.form.labels.location")}*`}
                        options={locations}
                        multiple={true}
                        isOptionEqualToValue={(option, value) => option.code === value.code}
                        handleChange={handleChangeLocations}
                    />
                </Grid>
            </Grid>
        </FormProvider>
    );

    const toolbar = {
        renderActions: () => (
            <BasicToolbar
                addIcon={
                    !!user.profiles.find((profile: any) => {
                        return profile.auths.find((auth: any) => {
                            return auth.name === "SCOPE_PRC_CREATE_LOCATION_GROUP_LOCATION";
                        });
                    })
                        ? {
                            onClick: async () => {
                                try {
                                    if (selectedLocationGroupLocation) {
                                        const locationGroup =
                                            selectedLocationGroupLocation?.locationGroup;
                                        handleChangeLocationGroup({
                                            code: locationGroup.id,
                                            label: locationGroup.name,
                                            legalEntity: locationGroup.legalEntity
                                        });
                                    }

                                    setFormDialog({
                                        open: true,
                                        onClickConfirm: form.handleSubmit(
                                            handleConfirmSaveLocationGroupLocation
                                        ),
                                        readonly: false,
                                    });
                                } catch (e) {
                                    console.log("Error: ", e);
                                }
                            },
                        }
                        : undefined
                }
            />
        ),
    };

    const resetTableOnPageChange = () => {
        locationGroupGridRef?.current?.api?.deselectAll();
        setSelectedLocationGroupLocation(null);
    };

    return (
        <Box>
            <SectionHeader title={t("translation:auths.SCOPE_PRC_VIEW_LOCATION_GROUP_LOCATION.name")}/>
            {formDialog?.open &&
                <Dialog open={formDialog.open} maxWidth={"lg"} fullWidth>
                    <BootstrapDialogTitle id="customized-dialog-title" onClose={() => {
                        form.reset();
                        setFormDialog(null);
                    }}>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            {t("pricing:locations.section.titles.addLocationToGroup")}
                        </Box>
                    </BootstrapDialogTitle>
                    <DialogContent style={{color: theme.palette.primary.main}}>
                        {formLocationGroupLocation}
                    </DialogContent>
                    <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                        <Button variant="outlined"
                                color={"accent"}
                                onClick={() => {
                                    form.reset();
                                    setFormDialog(null);
                                }}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={formDialog?.onClickConfirm}
                        >
                            {t("pricing:actions.confirm")}
                        </Button>
                    </DialogActions>
                </Dialog>
            }
            <SearchFilters
                filtersOutOfCollapse={filters}
                filtersInCollapse={[]}
                handleClickSearch={handleTriggerFiltersInitial}
                handleClickResetSearch={handleTriggerResetFilters}
            />

            <Grid container spacing={2} p={2}>
                <Grid item xs={12} md={6}>
                    <AgGridCustomTable
                        gridRef={locationGroupGridRef}
                        columnDefs={locationGroupColumnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={toolbar}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        rowModelType="serverSide"
                        paginationPageSize={10}
                        cacheBlockSize={10}
                        serverSideInfiniteScroll
                        onPaginationChanged={resetTableOnPageChange}
                        onSelectionChanged={onGroupSelectionChange}
                        rowSelection="single"
                        onGridReady={() => onGridReady({
                            initialSortColId: "locationGroupName",
                            gridRef: locationGroupGridRef,
                            createDatasource
                        })}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <AgGridCustomTable
                        gridRef={locationGridRef}
                        rowData={selectedLocationGroupLocation?.locations || []}
                        columnDefs={locationColumnDefs}
                        defaultColDef={defaultColDef}
                        tooltipShowDelay={0}
                        toolbar={{
                            renderActions: () => (
                                <div style={{overflow: "hidden"}}>
                                    <AddIcon sx={{color: "transparent", overflow: "hidden"}}/>
                                </div>)
                        }}
                        pagination
                        localeText={agGridLocale}
                        domLayout="autoHeight"
                        paginationPageSize={10}
                    />
                </Grid>
            </Grid>

            {dialog?.open &&
                <DialogComponent
                    type={"info"}
                    onClickConfirm={dialog.onClickConfirm}
                    setOpen={() => {
                        setDialog(null);
                    }}
                    open={dialog.open}
                    message={dialog.message}
                />
            }

            <Loading/>
        </Box>
    );
};

export default LocationGroupLocation;
